import { ThemeEvents } from '@theme/events';

/**
 * Matches `layout/theme.liquid` body class `template-product--shoes` (product template suffix `shoes`).
 * Full suit gate is not used on this PDP — see `full-suit-gate-dialog` render condition there.
 * @returns {boolean}
 */
function isShoesProductTemplate() {
  return document.body?.classList?.contains('template-product--shoes') === true;
}

/**
 * Main PDP only (excludes quick-add / cards that also use product-form).
 * @returns {boolean}
 */
function isProductTemplate() {
  return !!document.getElementById('ProductInfoWrapper');
}

/**
 * @returns {HTMLElement | null}
 */
function getMainProductVariantPicker() {
  return document.querySelector('#ProductInfoWrapper variant-picker');
}

/**
 * @returns {HTMLElement | null}
 */
function getEligibilityFallback() {
  return document.querySelector('#ProductInfoWrapper #FullSuitEligibilitySource');
}

/**
 * @returns {boolean}
 */
function getCanBuyNonFullSuit() {
  const picker = getMainProductVariantPicker();
  if (picker?.dataset.canBuyNonFullSuit != null) {
    return picker.dataset.canBuyNonFullSuit === 'true';
  }
  const fallback = getEligibilityFallback();
  if (fallback?.dataset.canBuyNonFullSuit != null) {
    return fallback.dataset.canBuyNonFullSuit === 'true';
  }
  return false;
}

/**
 * @returns {boolean}
 */
function isSelectedVariantFullSuit() {
  const picker = getMainProductVariantPicker();
  if (picker) {
    const checked = picker.querySelector('input[type="radio"]:checked');
    if (checked) return checked.hasAttribute('data-is-full-suit');
    const select = picker.querySelector('select.variant-option__select');
    if (select) {
      const opt = select.options[select.selectedIndex];
      if (opt) return opt.hasAttribute('data-is-full-suit');
    }
    return false;
  }
  const fallback = getEligibilityFallback();
  return fallback?.dataset.defaultVariantIsFullSuit === 'true';
}

/**
 * When true, non–full-suit variants must not be purchasable.
 * @returns {boolean}
 */
export function isFullSuitPurchaseBlocked() {
  if (isShoesProductTemplate()) return false;
  if (!isProductTemplate()) return false;
  if (getCanBuyNonFullSuit()) return false;
  return !isSelectedVariantFullSuit();
}

/**
 * @param {boolean} openDialog
 */
function applyFullSuitGate(openDialog) {
  if (!isProductTemplate()) return;

  const blocked = isFullSuitPurchaseBlocked();

  if (blocked) {
    for (const pfc of document.querySelectorAll('product-form-component')) {
      const atc = pfc.querySelector('add-to-cart-component');
      const accelerated = pfc.querySelector('[ref="acceleratedCheckoutButtonContainer"]');

      if (atc && typeof atc.disable === 'function') atc.disable();
      accelerated?.setAttribute('hidden', 'true');
    }
  }

  const mirror = document.querySelector('[data-pdp-mirror-atc]');
  if (mirror instanceof HTMLButtonElement) {
    if (blocked) {
      mirror.disabled = true;
    } else {
      const section = mirror.closest('.shopify-section');
      const mainBtn = section?.querySelector(
        'product-form-component add-to-cart-component [ref="addToCartButton"]'
      );
      if (mainBtn) mirror.disabled = mainBtn.disabled;
    }
  }

  if (blocked && openDialog) {
    void showFullSuitDialog();
  }
}

/**
 * @returns {Promise<void>}
 */
async function showFullSuitDialog() {
  await customElements.whenDefined('dialog-component');
  const el = document.getElementById('FullSuitGateDialog');
  if (el && 'showDialog' in el && typeof el.showDialog === 'function') {
    el.showDialog();
  }
}

function scheduleGate(openDialog) {
  requestAnimationFrame(() => {
    applyFullSuitGate(openDialog);
  });
}

function initFullSuitGate() {
  if (isShoesProductTemplate()) return;
  if (!isProductTemplate()) return;

  document.addEventListener(ThemeEvents.variantUpdate, () => scheduleGate(true));

  scheduleGate(false);
}

if (!isShoesProductTemplate()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFullSuitGate);
  } else {
    initFullSuitGate();
  }
}
